/**
 * AddWidgetPalette — FormModal listing the 9 addable BUILTIN kinds (the 3
 * unavailable kinds are excluded per plan Architecture point 2). Clicking a
 * kind creates the widget immediately via useCreateWidget (design brief:
 * "add/remove/rename commit immediately via their endpoints"), appended
 * below the lowest existing widget (RGL's own vertical compaction absorbs
 * any residual gaps). Default grid sizes match the V43 seed's own per-kind
 * w/h so a freshly-added widget is proportioned like its seeded sibling.
 */
import { toast } from 'sonner';
import { useCreateWidget } from '@/api/dashboardConfig';
import { MUTATION_ERROR_TOAST_ID } from '@/lib/toastIds';
import { FormModal } from '@/components/FormModal';
import type { BuiltinWidget, DashboardWidgetResponse } from '@/types/domain';

const ADDABLE_BUILTINS: { ref: BuiltinWidget; title: string; w: number; h: number }[] = [
  { ref: 'STAT_KPIS', title: 'Key metrics', w: 12, h: 2 },
  { ref: 'STATUS_DONUT', title: 'Work items by status', w: 4, h: 3 },
  { ref: 'HEALTH_DONUT', title: 'Project health', w: 4, h: 3 },
  { ref: 'THROUGHPUT', title: 'Weekly throughput', w: 4, h: 3 },
  { ref: 'CAPACITY_PANEL', title: 'Capacity', w: 6, h: 5 },
  { ref: 'RAID_BAR', title: 'Work items with open RAIDs', w: 6, h: 3 },
  { ref: 'PER_RESOURCE_CHART', title: 'Work items per resource', w: 12, h: 3 },
  { ref: 'ACCOUNTS_TABLE', title: 'Accounts', w: 12, h: 3 },
  { ref: 'PROJECT_RAG_TABLE', title: 'All projects', w: 12, h: 3 },
];

export interface AddWidgetPaletteProps {
  dashboardId: string;
  existingWidgets: DashboardWidgetResponse[];
  onClose: () => void;
  onAdded: () => void;
  /** Opens the METRIC widget editor (DashboardPage hosts it). Optional so
   *  existing render sites/tests keep compiling; the palette no-ops if unset. */
  onAddMetric?: () => void;
}

export function AddWidgetPalette({ dashboardId, existingWidgets, onClose, onAdded, onAddMetric }: AddWidgetPaletteProps) {
  const create = useCreateWidget();
  const nextY = existingWidgets.length === 0 ? 0 : Math.max(...existingWidgets.map((w) => w.gridY + w.gridH));

  function add(entry: (typeof ADDABLE_BUILTINS)[number]) {
    create.mutate(
      { dashboardId, kind: 'BUILTIN', builtinRef: entry.ref, title: entry.title, gridX: 0, gridY: nextY, gridW: entry.w, gridH: entry.h },
      {
        onSuccess: () => { toast.success(`Added "${entry.title}".`); onAdded(); },
        onError: () => toast.error("Couldn't add the widget.", { id: MUTATION_ERROR_TOAST_ID }),
      },
    );
  }

  return (
    <FormModal title="Add widget" onClose={onClose} width={420}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {ADDABLE_BUILTINS.map((entry) => (
          <button
            key={entry.ref}
            type="button"
            disabled={create.isPending}
            onClick={() => add(entry)}
            className="pressable focus-ring"
            style={{
              display: 'flex', alignItems: 'center', width: '100%', padding: '10px 12px',
              borderRadius: 'var(--radius-sm)', background: 'transparent', border: 'none', cursor: 'pointer',
              textAlign: 'left', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 500,
              color: 'var(--text)', opacity: create.isPending ? 0.6 : 1,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-inset)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            {entry.title}
          </button>
        ))}
        <div style={{ height: 1, background: 'var(--border)', margin: '6px 0' }} />
        <button
          type="button"
          disabled={create.isPending}
          onClick={() => onAddMetric?.()}
          className="pressable focus-ring"
          style={{
            display: 'flex', alignItems: 'center', width: '100%', padding: '10px 12px',
            borderRadius: 'var(--radius-sm)', background: 'transparent', border: 'none', cursor: 'pointer',
            textAlign: 'left', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 600,
            color: 'var(--brand)', opacity: create.isPending ? 0.6 : 1,
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-inset)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
        >
          + Metric widget…
        </button>
      </div>
    </FormModal>
  );
}
